import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { useLang } from "../LanguageContext";
import { api } from "../api";
import { Reveal, Overline, LineReveal } from "./Reveal";

const Field = ({ label, type = "text", value, onChange, textarea }) => (
  <label className="block group">
    <span className="overline text-white/50 group-focus-within:text-gold transition-colors">{label}</span>
    {textarea ? (
      <textarea rows={3} value={value} onChange={onChange}
        className="mt-3 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-white text-lg font-light transition-colors resize-none" />
    ) : (
      <input type={type} value={value} onChange={onChange}
        className="mt-3 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-white text-lg font-light transition-colors" />
    )}
  </label>
);

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const loadedAt = React.useRef(Date.now());
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, elapsed: Date.now() - loadedAt.current });
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "", website: "" });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      alert("Något gick fel. Försök igen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-ink text-white py-24 md:py-36">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 md:gap-24">
        <div>
          <Reveal><Overline dark>{t.contact.overline}</Overline></Reveal>
          <h2 lang={lang} className="font-serif font-light leading-[1.03] text-4xl md:text-6xl mt-7 [hyphens:auto] [overflow-wrap:break-word]">
            <LineReveal className="[hyphens:auto] [overflow-wrap:break-word]">{t.contact.title}</LineReveal>
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-8 text-white/70 text-lg font-light leading-relaxed max-w-md">{t.contact.body}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 space-y-4 text-white/60 font-light">
              {t.contact.email ? (
                <p><a href={`mailto:${t.contact.email}`} data-testid="contact-info-email" className="hover:text-gold-light transition-colors">{t.contact.email}</a></p>
              ) : null}
              {t.contact.phone ? (
                <p><a href={`tel:${t.contact.phone.replace(/\s+/g, "")}`} data-testid="contact-info-phone" className="hover:text-gold-light transition-colors">{t.contact.phone}</a></p>
              ) : null}
              {t.contact.address ? (
                <p data-testid="contact-info-address" className="whitespace-pre-line">{t.contact.address}</p>
              ) : null}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <Field label={t.contact.fields.name} value={form.name} onChange={set("name")} />
              <Field label={t.contact.fields.email} type="email" value={form.email} onChange={set("email")} />
            </div>
            <Field label={t.contact.fields.company} value={form.company} onChange={set("company")} />
            <Field label={t.contact.fields.message} value={form.message} onChange={set("message")} textarea />
            {/* Honeypot — hidden from humans, bots fill it */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={set("website")}
              data-testid="contact-honeypot"
              style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}
            />
            <button type="submit" disabled={sending} data-testid="contact-submit-button"
              className="group flex items-center gap-3 bg-gold hover:bg-gold-light text-white px-9 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors duration-300 disabled:opacity-70">
              {sending ? <Loader2 size={16} className="animate-spin" /> : null}
              {t.contact.fields.send}
              {!sending && <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />}
            </button>
            <AnimatePresence>
              {sent && (
                <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-2 text-gold-light text-sm">
                  <Check size={16} /> {t.contact.success}
                </motion.p>
              )}
            </AnimatePresence>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
